import { useState, useEffect, useCallback } from 'react';
import { useProviderAuth } from '../../contexts/ProviderAuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  FileText, Upload, CheckCircle, XCircle, AlertTriangle, 
  Trash2, Eye, Loader2, FileCheck, Info 
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document status colors
const DOC_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  verified: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800'
};

export function DocumentUploader() {
  const { token } = useProviderAuth();
  const [documents, setDocuments] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [recommendedDocs, setRecommendedDocs] = useState([]);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [documentTypes, setDocumentTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch documents and requirements
  const fetchDocuments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/provider/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      setDocuments(data.documents || []);
      setRequiredDocs(data.required || []);
      setRecommendedDocs(data.recommended || []);
      setCategoryInfo(data.category_info || null);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  }, [token]);

  // Fetch document types reference
  const fetchDocumentTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/provider/reference/document-types`);
      const data = await res.json();
      setDocumentTypes(data.document_types || {});
    } catch (err) {
      console.error('Error fetching document types:', err);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchDocuments(), fetchDocumentTypes()])
      .finally(() => setLoading(false));
  }, [fetchDocuments, fetchDocumentTypes]);

  // Get document label
  const getDocLabel = (docType) => {
    const type = documentTypes[docType];
    return type ? type.it : docType;
  };

  // Check if document type is already uploaded
  const isDocUploaded = (docType) => {
    return documents.some(d => d.doc_type === docType);
  };

  // Get uploaded doc info for type
  const getUploadedDoc = (docType) => {
    return documents.find(d => d.doc_type === docType);
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error('Formato non supportato. Usa PDF, JPEG o PNG.');
        return;
      }
      
      // Validate size (15MB max)
      if (file.size > 15 * 1024 * 1024) {
        toast.error('File troppo grande. Massimo 15 MB.');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile || !selectedType) {
      toast.error('Seleziona tipo documento e file');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('doc_type', selectedType);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const res = await fetch(`${API_URL}/api/provider/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Errore durante il caricamento');
      }

      toast.success('Documento caricato con successo!');
      setSelectedFile(null);
      setSelectedType('');
      
      // Refresh documents
      await fetchDocuments();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle delete
  const handleDelete = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) return;

    try {
      const res = await fetch(`${API_URL}/api/provider/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Errore durante eliminazione');
      }

      toast.success('Documento eliminato');
      await fetchDocuments();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Calculate completion percentage
  const completionPercent = requiredDocs.length > 0
    ? Math.round((requiredDocs.filter(d => isDocUploaded(d.type)).length / requiredDocs.length) * 100)
    : 100;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-umbria-olive" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-umbria-olive" />
              Documenti di Verifica
            </CardTitle>
            <CardDescription>
              Carica i documenti richiesti per la verifica del tuo profilo
            </CardDescription>
          </div>
          {categoryInfo?.sensitive && (
            <Badge variant="destructive" className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Categoria Sensibile
            </Badge>
          )}
        </div>

        {/* Progress bar */}
        {requiredDocs.length > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-umbria-muted">Documenti obbligatori</span>
              <span className="font-medium">{completionPercent}%</span>
            </div>
            <Progress value={completionPercent} className="h-2" />
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Required Documents Section */}
        {requiredDocs.length > 0 && (
          <div>
            <h4 className="font-medium text-umbria-charcoal mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              Documenti Obbligatori
            </h4>
            <div className="grid gap-2">
              {requiredDocs.map(doc => {
                const uploaded = getUploadedDoc(doc.type);
                return (
                  <div 
                    key={doc.type}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      uploaded ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {uploaded ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      )}
                      <div>
                        <p className="font-medium text-sm">{doc.label_it}</p>
                        {uploaded && (
                          <p className="text-xs text-umbria-muted">
                            {uploaded.verified ? 'Verificato' : 'In attesa di verifica'}
                          </p>
                        )}
                      </div>
                    </div>
                    {uploaded && (
                      <div className="flex items-center gap-2">
                        <Badge className={uploaded.verified ? DOC_STATUS_COLORS.verified : DOC_STATUS_COLORS.pending}>
                          {uploaded.verified ? 'Verificato' : 'In revisione'}
                        </Badge>
                        <a
                          href={`${API_URL}${uploaded.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                        {!uploaded.verified && (
                          <button
                            onClick={() => handleDelete(uploaded.id)}
                            className="p-1 hover:bg-red-100 rounded text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recommended Documents Section */}
        {recommendedDocs.length > 0 && (
          <div>
            <h4 className="font-medium text-umbria-charcoal mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-yellow-500" />
              Documenti Consigliati
            </h4>
            <div className="grid gap-2">
              {recommendedDocs.map(doc => {
                const uploaded = getUploadedDoc(doc.type);
                return (
                  <div 
                    key={doc.type}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      uploaded ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {uploaded ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Info className="w-5 h-5 text-yellow-500" />
                      )}
                      <p className="font-medium text-sm">{doc.label_it}</p>
                    </div>
                    {uploaded && (
                      <div className="flex items-center gap-2">
                        <Badge className={uploaded.verified ? DOC_STATUS_COLORS.verified : DOC_STATUS_COLORS.pending}>
                          {uploaded.verified ? 'Verificato' : 'In revisione'}
                        </Badge>
                        <a
                          href={`${API_URL}${uploaded.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                        {!uploaded.verified && (
                          <button
                            onClick={() => handleDelete(uploaded.id)}
                            className="p-1 hover:bg-red-100 rounded text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upload Section */}
        <div className="border-t pt-6">
          <h4 className="font-medium text-umbria-charcoal mb-4">Carica Documento</h4>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-umbria-muted mb-2 block">
                Tipo Documento
              </label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {/* Required first */}
                  {requiredDocs.filter(d => !isDocUploaded(d.type)).map(doc => (
                    <SelectItem key={doc.type} value={doc.type}>
                      {doc.label_it} (obbligatorio)
                    </SelectItem>
                  ))}
                  {/* Then recommended */}
                  {recommendedDocs.filter(d => !isDocUploaded(d.type)).map(doc => (
                    <SelectItem key={doc.type} value={doc.type}>
                      {doc.label_it}
                    </SelectItem>
                  ))}
                  {/* Allow other types */}
                  <SelectItem value="other">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-umbria-muted mb-2 block">
                File (PDF, JPEG, PNG)
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-white">
                  <FileText className="w-4 h-4 text-umbria-muted" />
                  <span className="text-sm truncate">
                    {selectedFile ? selectedFile.name : 'Scegli file...'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Upload progress */}
          {uploading && (
            <div className="mt-4">
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-sm text-umbria-muted mt-1 text-center">
                Caricamento in corso...
              </p>
            </div>
          )}

          <Button
            onClick={handleUpload}
            disabled={!selectedFile || !selectedType || uploading}
            className="mt-4 w-full sm:w-auto"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Caricamento...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Carica Documento
              </>
            )}
          </Button>
        </div>

        {/* Existing Documents */}
        {documents.length > 0 && (
          <div className="border-t pt-6">
            <h4 className="font-medium text-umbria-charcoal mb-4">
              Documenti Caricati ({documents.length})
            </h4>
            <div className="space-y-2">
              {documents.map(doc => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-umbria-olive" />
                    <div>
                      <p className="font-medium text-sm">{getDocLabel(doc.doc_type)}</p>
                      <p className="text-xs text-umbria-muted">
                        {new Date(doc.uploaded_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={doc.verified ? DOC_STATUS_COLORS.verified : DOC_STATUS_COLORS.pending}>
                      {doc.verified ? 'Verificato' : 'In revisione'}
                    </Badge>
                    <a
                      href={`${API_URL}${doc.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-gray-200 rounded"
                      title="Visualizza"
                    >
                      <Eye className="w-4 h-4" />
                    </a>
                    {!doc.verified && (
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="p-2 hover:bg-red-100 rounded text-red-600"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
